import React, { useMemo } from 'react';
import { generatePath, useLocation, useRouteMatch } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { Transition, TransitionGroup } from 'react-transition-group';
import cx from 'classnames';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { CDN_TYPE_SPORT, getCDNUrl } from 'utils';

import {
  Bus,
  EventProvider,
  isEmptyCollection,
  isEventOffline,
  isNil,
  isVisible,
  useBetslipState,
  useEventState,
  useTournamentState,
} from '@atlas/core';
import { EventLink } from '@atlas/routing';
import {
  createEventSelectableView,
  EventDetailedScore,
  EventLiveParticipants,
  EventNoView,
  Flag,
  MarketPlaceholder,
  MarketType,
  MarketTypeContainer,
  Stepper,
  TournamentEventsPlaceholder,
  useTabNavigator,
} from '@atlas/ui';

import { SPORT_IDS_WITH_DETAILED_SCORE } from 'common/constants';
import EventPeriodInfo from 'common/Widgets/Tournament/Tournament/Events/Event/EventPeriodInfo';
import { outcomeNameParser } from 'components/Pages/TournamentScrollGrid';

const MarketTypeSwipeTransition = props => {
  const { children, left, right } = props;

  const containerClassName = cx('swipe-animation-container', { left, right });

  return <TransitionGroup className={containerClassName}>{children}</TransitionGroup>;
};

const MarketTypeSwipeView = React.forwardRef((props, ref) => {
  const { className, hasSportFormMainTypes, marketTypes, suspended } = props;

  const [index] = useTabNavigator();

  const marketType = Object.values(marketTypes)[index];

  if (!marketType) {
    return <div className={className} ref={ref} />;
  }

  if (isEmptyCollection(marketType.markets)) {
    return (
      <MarketTypeContainer
        className={className}
        hidePriority={marketType.hidePriority}
        outcomeTypes={marketType.outcomeTypes}
        ref={ref}
      >
        <MarketPlaceholder outcomeTypes={marketType.outcomeTypes} />
      </MarketTypeContainer>
    );
  }

  return (
    <MarketType
      className={className}
      hasSportFormMainTypes={hasSportFormMainTypes}
      marketType={marketType}
      outcomeNameParser={outcomeNameParser}
      ref={ref}
      suspended={suspended}
    />
  );
});

export const TournamentMainHeaderComponent = () => {
  const { id, name } = useTournamentState();

  return (
    <div className="tournament-info">
      <span className="tournament-flag-icon">
        <Flag
          alt={name}
          cdnHostname={getCDNUrl(CDN_TYPE_SPORT)}
          className="flag__sport"
          name={id}
        />
      </span>

      <span className="tournament-name">{name}</span>
    </div>
  );
};

export const EventSwipeGrid = createEventSelectableView({
  EventHeaderComponent({ marketTypes }) {
    const stateAndDispatch = useTabNavigator();
    const { id } = useEventState();

    const index = useMemo(() => {
      if (Array.isArray(stateAndDispatch)) {
        return stateAndDispatch[0];
      }
    }, [stateAndDispatch]);

    const setIndex = useMemo(() => {
      if (Array.isArray(stateAndDispatch)) {
        return stateAndDispatch[1];
      }
    }, [stateAndDispatch]);

    React.useEffect(() => {
      // If market types provided
      if (Array.isArray(marketTypes)) {
        // Reset if marketType not exist
        if (index > 0 && isNil(marketTypes[index])) {
          setIndex(0);
          return;
        }
      }

      const swipeHandler = data => {
        if (data.dir === 'Right' && marketTypes[index - 1]) {
          return setIndex(index => index - 1);
        }

        if (data.dir === 'Left' && marketTypes[index + 1]) {
          return setIndex(index => index + 1);
        }
      };

      Bus.on(`ui:swipe:${id}`, swipeHandler);
      return () => Bus.off(`ui:swipe:${id}`, swipeHandler);
    }, [id, marketTypes, index, setIndex]);

    const marketTypeSwitchComponent = React.useMemo(() => {
      // Validating
      if (!Array.isArray(marketTypes)) {
        return null;
      }

      const marketType = marketTypes[index];
      const onClickHandler = nextIndex => {
        setIndex(nextIndex);
      };

      return (
        <Stepper
          hideArrows={marketTypes?.length === 1}
          index={index}
          maxIndex={marketTypes?.length - 1}
          minIndex={0}
          onClick={onClickHandler}
          value={marketType?.name}
        />
      );
    }, [index, marketTypes, setIndex]);

    return (
      <div className="event-header container">
        <EventPeriodInfo swipe />

        <div className="event__market-types">{marketTypeSwitchComponent}</div>
      </div>
    );
  },
  EventMarketTypeGridComponent(props) {
    const { className, hasSportFormMainTypes, marketTypes, suspended } = props;

    const nodeRef = React.useRef();
    const [index] = useTabNavigator();
    const [prevIndex, setPrevIndex] = React.useState(index);
    const onEnteredHandler = () => {
      setPrevIndex(index);
    };

    return (
      <MarketTypeSwipeTransition left={index < prevIndex} right={index > prevIndex}>
        <Transition
          addEndListener={done => {
            // Using CSS transitionend event to mark the finish of a transition
            if (nodeRef.current) {
              nodeRef.current.addEventListener('transitionend', done, false);
            }
          }}
          className="swipe"
          key={index}
          nodeRef={nodeRef}
          onEntered={onEnteredHandler}
          timeout={500}
        >
          {state => (
            <MarketTypeSwipeView
              className={cx(className, state)}
              hasSportFormMainTypes={hasSportFormMainTypes}
              marketTypes={marketTypes}
              ref={nodeRef}
              suspended={suspended}
            />
          )}
        </Transition>
      </MarketTypeSwipeTransition>
    );
  },
  EventMainInfoComponent() {
    const { search } = useLocation();
    const eventLinkPostfix = search.includes('tab=') ? `&tab=${qs.parse(search).tab}` : '';
    const { id, info, participants, sportId } = useEventState();

    const { categoryUrlName, sportUrlName, urlName } = useTournamentState();
    const { path } = useRouteMatch();

    const toUrl = generatePath(path, {
      categoryName: categoryUrlName,
      sportName: sportUrlName,
      tournamentName: urlName,
    });

    const LiveParticipantsComponent = React.useMemo(
      () =>
        SPORT_IDS_WITH_DETAILED_SCORE.includes(sportId) ? (
          <EventDetailedScore participants={participants} score={info.score} stats={info.stats} />
        ) : (
          <EventLiveParticipants participants={participants} score={info.score} variant="grid" />
        ),
      [info, participants, sportId],
    );

    return (
      <div className="event-period__info">
        <div className="event-period-main">
          <EventLink
            className="event-period__link"
            customLocation={toUrl}
            dataTestId="event"
            dataTestItemId={id}
            eventId={`${id}${eventLinkPostfix}`}
          >
            {LiveParticipantsComponent}
          </EventLink>
        </div>
      </div>
    );
  },
  EventPeriodInfoComponent({ eventId, marketTypeInstruction }) {
    const { search } = useLocation();
    const eventLinkPostfix = search.includes('tab=') ? `&tab=${qs.parse(search).tab}` : '';
    const { categoryUrlName, sportUrlName, urlName } = useTournamentState();
    const { path } = useRouteMatch();

    const toUrl = generatePath(path, {
      categoryName: categoryUrlName,
      sportName: sportUrlName,
      tournamentName: urlName,
    });

    return (
      <div className="event-period__info">
        <EventLink
          className="event-period-sub"
          customLocation={toUrl}
          eventId={`${eventId}${eventLinkPostfix}`}
        >
          <div className="event-period__name">{marketTypeInstruction.periodTypeName}</div>
          <div className="event-period__score" />
        </EventLink>
      </div>
    );
  },
  EventPeriodExtraComponent: EventNoView,
  EventLink,
});

const TournamentEvents = props => {
  const {
    eventClassName,
    eventProvider,
    events,
    feeds,
    hasSportFormMainTypes,
    marketTypeClassName,
    marketTypesPeriodMap,
    onEventClick,
    outcomeClassName,
  } = props;

  return (
    Object.values(events)
      // Show only visible default events
      .filter(event => isVisible(event) && !event.outright)
      // ascending sorting order
      .sort((a, b) => a.startTime - b.startTime)
      .map(event => {
        const { source, sportId, sportService } = event;
        const suspended = isEventOffline({ feeds, source, sportId, sportService });

        return (
          <TournamentEvent
            event={event}
            eventClassName={eventClassName}
            eventProvider={eventProvider}
            hasSportFormMainTypes={hasSportFormMainTypes}
            key={event.id}
            marketTypeClassName={marketTypeClassName}
            marketTypesPeriodMap={marketTypesPeriodMap}
            onEventClick={onEventClick}
            outcomeClassName={outcomeClassName}
            suspended={suspended}
          />
        );
      })
  );
};

const TournamentEvent = props => {
  const {
    event,
    eventClassName,
    eventProvider: TournamentEventProvider = EventProvider,
    hasSportFormMainTypes,
    marketTypeClassName,
    marketTypesPeriodMap,
    onEventClick,
    outcomeClassName,
    suspended,
  } = props;

  const { ref } = useSwipeable({
    onSwipedLeft: data => Bus.emit(`ui:swipe:${event.id}`, data),
    onSwipedRight: data => Bus.emit(`ui:swipe:${event.id}`, data),
    trackMouse: true,
    trackTouch: true,
  });

  return (
    <TournamentEventProvider event={event}>
      <EventSwipeGrid
        className={eventClassName}
        hasSportFormMainTypes={hasSportFormMainTypes}
        marketTypeClassName={marketTypeClassName}
        marketTypesPeriodMap={marketTypesPeriodMap}
        onEventClick={onEventClick}
        outcomeClassName={outcomeClassName}
        ref={ref}
        suspended={suspended}
      />
    </TournamentEventProvider>
  );
};

TournamentEvent.propTypes = {
  event: PropTypes.object.isRequired,
  eventClassName: PropTypes.string,
  eventProvider: PropTypes.elementType,
  hasSportFormMainTypes: PropTypes.bool,
  marketTypeClassName: PropTypes.string,
  marketTypesPeriodMap: PropTypes.array.isRequired,
  onEventClick: PropTypes.func.isRequired,
  outcomeClassName: PropTypes.string,
  suspended: PropTypes.bool,
};

const renderSelectableEventPlaceholder = (marketTypesPlaceholder, index) => (
  <div className="event swipe event_placeholder" key={index}>
    <div className="event-header container">
      <div className="event-period__info" />

      <div className="event__market-types" />
    </div>

    <div className="event-content">
      <div className="event-period">
        <div className="event-period__info">
          <div className="event-period-main" />
        </div>

        <div className="event__market-types">{marketTypesPlaceholder}</div>
      </div>
    </div>
  </div>
);

const TournamentSwipeEventsPlaceholder = props => {
  const { count, marketTypesPeriodMap } = props;

  return (
    <TournamentEventsPlaceholder
      count={count}
      marketTypesPeriodMap={marketTypesPeriodMap.map(marketTypes => marketTypes.slice(0, 1))}
      renderEventPlaceholder={renderSelectableEventPlaceholder}
    />
  );
};

export const TournamentSwipeGrid = props => {
  const {
    className,
    eventClassName = 'swipe',
    eventProvider,
    hasSportFormMainTypes,
    marketTypeClassName,
    marketTypesPeriodMap,
    onEventClick,
    outcomeClassName,
  } = props;

  const {
    events,
    id,
    initialized,
    liveCount = 0,
    loading,
    prematchCount = 0,
  } = useTournamentState();

  const { settings } = useBetslipState();
  /*
      Validating tournament state
    */
  const TournamentEventsView =
    loading && !initialized ? TournamentSwipeEventsPlaceholder : TournamentEvents;

  const tournamentClassName = cx('tournament', className);

  return (
    <div className={tournamentClassName} data-tid={id}>
      <TournamentEventsView
        count={prematchCount + liveCount}
        eventClassName={eventClassName}
        eventProvider={eventProvider}
        events={events}
        feeds={settings.feeds}
        hasSportFormMainTypes={hasSportFormMainTypes}
        marketTypeClassName={marketTypeClassName}
        marketTypesPeriodMap={marketTypesPeriodMap}
        onEventClick={onEventClick}
        outcomeClassName={outcomeClassName}
      />
    </div>
  );
};

TournamentSwipeGrid.propTypes = {
  className: PropTypes.string,
  eventClassName: PropTypes.string,
  eventProvider: PropTypes.elementType,
  hasSportFormMainTypes: PropTypes.bool,
  marketTypeClassName: PropTypes.string,
  marketTypesPeriodMap: PropTypes.array.isRequired,
  onEventClick: PropTypes.func.isRequired,
  outcomeClassName: PropTypes.string,
};
